import { useState, useCallback } from 'react';
import {
  getDiagnosticOrderPatients,
  selectDiagnosticOrderPatients,
} from '../services/subscriberWalletService';

export const useDiagnosticPatients = (currentRole) => {
  const [members, setMembers] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [savingPatients, setSavingPatients] = useState(false);

  const loadPatients = useCallback(async (orderId, showNotification, autoSelectId = null, preserveSelection = false) => {
    if (!orderId) return;
    setLoadingPatients(true);
    try {
      const response = await getDiagnosticOrderPatients(currentRole, orderId);
      if (response?.metadata?.api_status === true) {
        const memberList = response.msg?.members || [];
        setMembers(memberList);
        if (!preserveSelection) {
          const selectedFromApi = memberList.filter(m => m.selected).map(m => m.id);
          const ids = autoSelectId && !selectedFromApi.includes(autoSelectId)
            ? [...selectedFromApi, autoSelectId]
            : selectedFromApi;
          setSelectedMemberIds(ids);
        } else if (autoSelectId) {
          setSelectedMemberIds(prev =>
            prev.includes(autoSelectId) ? prev : [...prev, autoSelectId]
          );
        }
      } else {
        throw new Error(response?.msg?.error || 'Failed to load patients');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to load patients', 'error');
    } finally {
      setLoadingPatients(false);
    }
  }, [currentRole]);

  const toggleMember = useCallback((memberId) => {
    setSelectedMemberIds(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  }, []);

  const savePatients = useCallback(async (orderId, showNotification) => {
    if (!orderId || selectedMemberIds.length === 0) {
      showNotification('Please select at least one patient', 'error');
      return false;
    }
    setSavingPatients(true);
    try {
      const response = await selectDiagnosticOrderPatients(currentRole, orderId, selectedMemberIds);
      if (response?.metadata?.api_status === true) {
        showNotification('Patients saved successfully', 'success');
        return true;
      } else {
        throw new Error(response?.msg?.error || 'Failed to save patients');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to save patients', 'error');
      return false;
    } finally {
      setSavingPatients(false);
    }
  }, [currentRole, selectedMemberIds]);

  return {
    members,
    selectedMemberIds,
    loadingPatients,
    savingPatients,
    loadPatients,
    toggleMember,
    savePatients,
  };
};
